import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../services/supabase';
import { Mail, Lock, LogIn, UserPlus, Eye, EyeOff, ArrowLeft, Send, CheckCircle2, AlertCircle, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

type AuthView = 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'verify-otp';

export const Auth: React.FC = () => {
    const [view, setView] = useState<AuthView>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    React.useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const urlView = searchParams.get('view') as AuthView;
        if (urlView && ['login', 'signup', 'forgot-password', 'reset-password', 'verify-otp'].includes(urlView)) {
            setView(urlView);
        }

        // Handle #access_token=... (from recovery email links)
        if (window.location.hash.includes('type=recovery') || window.location.hash.includes('access_token')) {
            setView('reset-password');
        }
    }, []);

    const handleGoogleAuth = async () => {
        try {
            const searchParams = new URLSearchParams(window.location.search);
            const redirectToParam = searchParams.get('redirect_to');
            const redirectTo = redirectToParam || `${window.location.origin}/dashboard`;

            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: redirectTo
                }
            });
            if (error) throw error;
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Google Auth Error' });
        }
    };

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            if (view === 'login') {
                const { error } = await supabase.auth.signInWithPassword({ email, password });
                if (error) {
                    if (error.message.includes('Email not confirmed')) {
                        throw new Error('Email verification record found. Please check your inbox and verify your email.');
                    }
                    throw error;
                }

                const searchParams = new URLSearchParams(window.location.search);
                const redirectUrl = searchParams.get('redirect_to');
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }
            } else if (view === 'signup') {
                const { error } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                        data: {
                            tier: 'Free',
                            api_calls_used: 0
                        }
                    }
                });
                if (error) throw error;
                setMessage({ type: 'success', text: 'Registration successful! Verification email sent. Please check your inbox.' });
                setTimeout(() => setView('login'), 3000);
            } else if (view === 'forgot-password') {
                const { error } = await supabase.auth.resetPasswordForEmail(email, {
                    redirectTo: `${window.location.origin}/login?view=reset-password`,
                });
                if (error) throw error;
                setMessage({ type: 'success', text: 'Reset link and code sent to your email.' });
                setTimeout(() => setView('verify-otp'), 2000);
            } else if (view === 'verify-otp') {
                const { error } = await supabase.auth.verifyOtp({
                    email,
                    token: otp,
                    type: 'recovery',
                });
                if (error) throw error;
                setMessage({ type: 'success', text: 'Verification successful!' });
                setView('reset-password');
            } else if (view === 'reset-password') {
                const { error } = await supabase.auth.updateUser({ password });
                if (error) throw error;
                setMessage({ type: 'success', text: 'Password updated successfully! Redirecting to login...' });
                setTimeout(() => setView('login'), 2000);
            }
        } catch (err: any) {
            setMessage({ type: 'error', text: err.message || 'Authentication error' });
        } finally {
            setLoading(false);
        }
    };

    const StatusNotification = () => {
        if (!message) return null;
        const isSuccess = message.type === 'success';
        return (
            <div className={`flex items-start gap-3 p-4 rounded-xl border mb-6 animate-in fade-in slide-in-from-top-4 duration-500 ${isSuccess ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'
                }`}>
                {isSuccess ? <CheckCircle2 className="w-5 h-5 shrink-0" /> : <AlertCircle className="w-5 h-5 shrink-0" />}
                <p className="text-sm font-medium">{message.text}</p>
            </div>
        );
    };

    const getHeaderContent = () => {
        switch (view) {
            case 'signup': return { title: 'Create Account', desc: 'Join to unlock AI-powered trading signals' };
            case 'forgot-password': return { title: 'Reset Password', desc: "Enter your email and we'll send you a recovery link" };
            case 'verify-otp': return { title: 'Verify Code', desc: 'Enter the 6-digit code sent to your email' };
            case 'reset-password': return { title: 'New Password', desc: 'Secure your account with a new password' };
            default: return { title: 'Welcome Back', desc: 'Sign in to access institutional insights' };
        }
    };

    const { title, desc } = getHeaderContent();

    return (
        <div className="min-h-screen bg-finance-dark flex items-center justify-center p-4">
            <Helmet>
                <title>Secure Login — ForexFund AI Institutional Dashboard</title>
                <meta name="description" content="Log in to your ForexFund AI account to access real-time fundamental bias, order flow maps, and institutional signals. Start trading with an institutional edge." />
                <link rel="canonical" href="https://www.forexfundai.com/login" />
            </Helmet>
            <div className="bg-finance-card/80 backdrop-blur-md p-8 rounded-2xl border border-gray-800 shadow-2xl w-full max-w-md">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-white tracking-tight mb-2">
                        {title}
                    </h2>
                    <p className="text-gray-400 text-sm font-medium tracking-wide">
                        {desc}
                    </p>
                </div>

                <StatusNotification />

                {(view === 'login' || view === 'signup') && (
                    <>
                        <button
                            onClick={handleGoogleAuth}
                            type="button"
                            className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-900 font-bold rounded-xl transition-all border border-transparent shadow-lg mb-6 active:scale-[0.98]"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                            Continue with Google
                        </button>

                        <div className="relative flex items-center py-2 mb-6">
                            <div className="flex-grow border-t border-gray-800"></div>
                            <span className="flex-shrink-0 mx-4 text-gray-500 text-xs font-bold uppercase tracking-widest">or email auth</span>
                            <div className="flex-grow border-t border-gray-800"></div>
                        </div>
                    </>
                )}

                <form onSubmit={handleAuth} className="space-y-5">
                    {(view !== 'reset-password' && view !== 'verify-otp') && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">Email Address</label>
                            <div className="relative group">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 group-focus-within:text-blue-400 transition-colors" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl pl-12 pr-4 py-3.5 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-gray-600"
                                    placeholder="Enter your email"
                                />
                            </div>
                        </div>
                    )}

                    {view === 'verify-otp' && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 ml-1">Verification Code</label>
                            <div className="relative group">
                                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 group-focus-within:text-yellow-400 transition-colors" />
                                <input
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    required
                                    className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl pl-12 pr-4 py-3.5 text-center text-2xl tracking-[10px] font-bold focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition-all"
                                    placeholder="000000"
                                />
                            </div>
                        </div>
                    )}

                    {(view === 'login' || view === 'signup' || view === 'reset-password') && (
                        <div>
                            <div className="flex justify-between items-center mb-2 ml-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                                    {view === 'reset-password' ? 'New Password' : 'Password'}
                                </label>
                                {view === 'login' && (
                                    <button
                                        type="button"
                                        onClick={() => setView('forgot-password')}
                                        className="text-xs font-bold text-blue-400 hover:text-blue-300 transition-colors"
                                    >
                                        Forgot?
                                    </button>
                                )}
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5 group-focus-within:text-emerald-400 transition-colors" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl pl-12 pr-12 py-3.5 focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all placeholder:text-gray-600"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white focus:outline-none"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                    )}

                    {view === 'signup' && (
                        <div className="flex items-start gap-3 mt-2 mb-4">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                className="mt-1 w-4 h-4 rounded border-gray-800 bg-[#0b0f19] text-finance-accent focus:ring-finance-accent"
                                required
                            />
                            <label htmlFor="terms" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                                I agree to the <Link to="/terms?source=signup" className="text-white hover:text-finance-accent font-bold underline underline-offset-2 transition-colors">Terms of Service</Link>, <Link to="/privacy?source=signup" className="text-white hover:text-finance-accent font-bold underline underline-offset-2 transition-colors">Privacy Policy</Link>, and <Link to="/disclaimer?source=signup" className="text-white hover:text-finance-accent font-bold underline underline-offset-2 transition-colors">Risk Disclaimer</Link>.
                            </label>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || (view === 'signup' && !agreedToTerms)}
                        className={`w-full h-12 flex items-center justify-center gap-2 font-bold rounded-xl transition-all active:scale-[0.98] disabled:opacity-50 mt-4 ${view === 'login' ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.2)]' :
                            view === 'signup' ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.2)]' :
                                'bg-finance-accent hover:bg-yellow-500 text-finance-darker'
                            }`}
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        ) : view === 'login' ? (
                            <><LogIn className="w-5 h-5" /> Sign In</>
                        ) : view === 'signup' ? (
                            <><UserPlus className="w-5 h-5" /> Create Account</>
                        ) : view === 'forgot-password' ? (
                            <><Send className="w-5 h-5" /> Send Reset Link</>
                        ) : view === 'verify-otp' ? (
                            <><ShieldCheck className="w-5 h-5" /> Verify Code</>
                        ) : (
                            <><ShieldCheck className="w-5 h-5" /> Update Password</>
                        )}
                    </button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-800/50 text-center">
                    {view === 'login' ? (
                        <p className="text-gray-500 text-sm font-medium">
                            Don't have an account?{' '}
                            <button onClick={() => setView('signup')} className="text-white font-bold hover:text-blue-400 transition-colors">Sign up</button>
                        </p>
                    ) : (
                        <button onClick={() => setView('login')} className="flex items-center gap-2 mx-auto text-gray-500 text-sm font-bold hover:text-white transition-colors">
                            <ArrowLeft className="w-4 h-4" /> Back to Sign In
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
